// ╔══════════════════════════════════════════════════════════════╗
// ║  Toast — global notification snackbar                          ║
// ╚══════════════════════════════════════════════════════════════╝

import { createContext, useCallback, useContext, useState } from 'react';
import clsx from 'clsx';
import Icon from './Icon.jsx';

const ToastCtx = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((t) => {
    const id = nextId++;
    const tone = t.tone || 'info';
    setToasts((cur) => [...cur, { id, tone, ...t }]);
    setTimeout(() => {
      setToasts((cur) => cur.filter((x) => x.id !== id));
    }, t.duration || 4000);
  }, []);

  const dismiss = useCallback((id) => {
    setToasts((cur) => cur.filter((x) => x.id !== id));
  }, []);

  return (
    <ToastCtx.Provider value={{ push, dismiss }}>
      {children}
      <div className="fixed top-4 left-4 right-4 sm:right-auto sm:left-auto sm:bottom-4 sm:top-auto z-[100] flex flex-col gap-2 max-w-md mx-auto sm:mx-0">
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

function ToastItem({ toast, onClose }) {
  const colors = {
    success: 'bg-emerald-500 text-white',
    warn: 'bg-amber-500 text-ink-950',
    danger: 'bg-rose-500 text-white',
    info: 'bg-ink-900 text-white dark:bg-ink-800',
  };
  const icons = {
    success: 'check',
    warn: 'alert',
    danger: 'alert',
    info: 'sparkle',
  };
  return (
    <div
      className={clsx(
        'rounded-2xl shadow-pop px-4 py-3 flex items-start gap-3 fade-up',
        colors[toast.tone],
      )}
      role="status"
    >
      <Icon name={icons[toast.tone]} size={18} className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {toast.title && <div className="font-bold text-sm">{toast.title}</div>}
        {toast.body && <div className="text-xs opacity-90 mt-0.5">{toast.body}</div>}
      </div>
      <button onClick={onClose} className="opacity-70 hover:opacity-100">
        <Icon name="x" size={14} />
      </button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be inside ToastProvider');
  return ctx;
}
